'use client'

import React, { useId } from 'react'

import { useExtractSpaceProps } from '../../hooks'
import { SpaceProps } from '../../themes'
import { Field } from '../Field'
import { TextField, TextFieldProps } from '../TextField'
import { Typography } from '../Typography'
import { Content } from './styles'

export interface TimeRangeFieldProps
  extends Omit<
      TextFieldProps,
      | 'value'
      | 'errorMessages'
      | 'captionMessages'
      | 'placeholder'
      | 'onChange'
      | 'onBlur'
    >,
    SpaceProps {
  /**
   * Class name
   */
  className?: string

  /**
   * Time label
   */
  label?: string

  /**
   * Range Value
   */
  value: {
    from?: string | null
    to?: string | null
  }

  /**
   * Required flag
   */
  required?: boolean

  /**
   * Left-side placeholder
   */
  fromPlaceholder?: string

  /**
   * Right-side placeholder
   */
  toPlaceholder?: string

  /**
   * Icon to display before the error message
   */
  errorIcon?: React.ReactNode

  /**
   * Error messages
   */
  errorMessages?: string | string[]

  /**
   * Icon to display before the caption message
   */
  captionIcon?: React.ReactNode

  /**
   * Caption messages
   */
  captionMessages?: string | string[]

  /**
   * Disable body scroll lock
   */
  disableBodyScrollLock?: boolean

  /**
   * onBlur handler (from)
   */
  onBlurFrom?: (e: React.FocusEvent) => void

  /**
   * onBlur handler (to)
   */
  onBlurTo?: (e: React.FocusEvent) => void

  /**
   * onChange handler
   */
  onChange: (value: { from?: string | null; to?: string | null }) => void
}

export const TimeRangeField = ({
  id,
  className,
  label,
  required,
  errorIcon,
  errorMessages,
  captionIcon,
  captionMessages,
  fromPlaceholder,
  toPlaceholder,
  value,
  onBlurFrom,
  onBlurTo,
  onChange,
  ...props
}: TimeRangeFieldProps) => {
  const autoId = useId()
  const inputId = id ?? autoId
  const [spaceProps, selectProps] = useExtractSpaceProps(props)

  return (
    <Field className={className} {...spaceProps}>
      {label && (
        <Field.Label id={inputId} required={required}>
          {label}
        </Field.Label>
      )}

      <Field.Content>
        <Content>
          <TextField
            id={`${inputId}-from`}
            type="time"
            placeholder={fromPlaceholder}
            value={value.from ?? ''}
            onChange={(e) =>
              onChange({ ...value, from: e.target.value || undefined })
            }
            onBlur={onBlurFrom}
            {...selectProps}
          />
          <Typography as="div" variant="caption1" color="gray.01">
            〜
          </Typography>
          <TextField
            id={`${inputId}-to`}
            type="time"
            placeholder={toPlaceholder}
            value={value.to ?? ''}
            onChange={(e) =>
              onChange({ ...value, to: e.target.value || undefined })
            }
            onBlur={onBlurTo}
            {...selectProps}
          />
        </Content>
      </Field.Content>

      <Field.Messages
        mt={2}
        type="error"
        messages={errorMessages}
        icon={errorIcon}
      />

      <Field.Messages
        mt={errorMessages || (errorMessages && errorMessages.length) ? 1 : 2}
        type="caption"
        messages={captionMessages}
        icon={captionIcon}
      />
    </Field>
  )
}
